// prefabMatcher — JS port of convorcher's prefab phrase matcher (ENG-843).
//
// Mirrors app/services/animation/animation.go (contentWords /
// KeywordsMatchPrefab) EXACTLY, so the live tester in the prefab editor
// predicts what the server will actually do. The quirks below are deliberate
// parity, not bugs — do not "fix" them here without changing the Go side:
//   - possessive suffixes ('s and ’s) are stripped ANYWHERE in a word
//     (strings.ReplaceAll), not just at word end
//   - punctuation is NOT stripped: "five!" is a different word than "five"
//   - splitting is on whitespace only (strings.Fields)
// Rule: a phrase fires when >= 50% of its words appear in the input.
// Vectors from convorcher's TestKeywordsMatchPrefab are pinned in
// tests/unit/prefabMatcher.test.mjs.

/** Go contentWords parity: lowercase, trim, strip possessives, split on whitespace. */
export function words(text) {
  return String(text ?? '')
    .toLowerCase()
    .trim()
    .replace(/'s|’s/g, '')
    .split(/\s+/)
    .filter(Boolean)
}

/** Score one phrase against an utterance: { hits, total, ok }. */
export function matchScore(input, phrase) {
  const pw = words(phrase)
  if (!pw.length) return { hits: 0, total: 0, ok: false }
  const set = new Set(words(input))
  const hits = pw.filter((w) => set.has(w)).length
  return { hits, total: pw.length, ok: hits / pw.length >= 0.5 }
}

/** Go KeywordsMatchPrefab parity: true if any phrase reaches 50% word overlap. */
export function keywordsMatchPrefab(userInput, phrases) {
  if (!words(userInput).length) return false
  return (Array.isArray(phrases) ? phrases : []).some((p) => matchScore(userInput, p).ok)
}

/**
 * Tester evaluation for the Triggering UI (pure — drives chip highlights and
 * the verdict line). `fired` is the FIRST matching phrase (what the server
 * would report); every matching index is listed so all qualifying chips light.
 * Exclusions are evaluated with the same rule; suppression is the PROPOSED
 * behavior — convorcher does not enforce notFor yet (see the UI hint).
 */
export function evaluateUtterance(input, phrases, exclusions) {
  const empty = { fired: null, blocked: null, phraseHits: [], exclusionHits: [] }
  if (!words(input).length) return empty

  const phraseHits = []
  let fired = null
  ;(phrases ?? []).forEach((p, i) => {
    const s = matchScore(input, p)
    if (!s.ok) return
    phraseHits.push(i)
    if (!fired) fired = { index: i, phrase: p, hits: s.hits, total: s.total }
  })

  const exclusionHits = []
  let blocked = null
  ;(exclusions ?? []).forEach((p, i) => {
    const s = matchScore(input, p)
    if (!s.ok) return
    exclusionHits.push(i)
    if (!blocked) blocked = { index: i, phrase: p }
  })

  return { fired, blocked, phraseHits, exclusionHits }
}
